import { getToken } from ".";
import { CompanyType } from "../../models/company";
import { HttpException } from "../../models/exceptions";
import { TaskTypeMapping, TaskTypesApiResponse } from "../../models/integrations";

// ### Fetching companies
export async function fetchCompanies({
  signal,
  status,
  service,
  integration,
  name,
  page = 1,
  size = 50,
  fields = "id,name,legacy_data.id,info.name,info.languages,info.website_url,status,services,integrations,config_design_temp",
  services_and_integrations_only_status = true,
}: {
  signal?: any;
  status?: string;
  service?: string;
  integration?: string;
  name?: string;
  page?: number;
  size?: number;
  fields?: string;
  services_and_integrations_only_status?: boolean;
}) {
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_API_HOST}/companies`;

  const params = new URLSearchParams();

  // Build query object from individual filters
  const query: Record<string, any> = {};
  if (status === "active_onboarding") query.status = { $in: ["active", "onboarding"] };
  else if (status) query.status = status;
  if (service) {
    query[`services.${service}`] = { $exists: true };
    // When filtering by service, exclude companies where that service is inactive
    query[`services.${service}.status`] = { $ne: "inactive" };
  }
  if (integration) {
    query[`integrations.${integration}`] = { $exists: true };
    // When filtering by integration, exclude companies where that integration is inactive
    query[`integrations.${integration}.status`] = { $ne: "inactive" };
  }
  if (name) query["info.name"] = { $regex: name, $options: "i" };

  // Add query param only if filters exist
  if (Object.keys(query).length > 0) {
    params.append("query", JSON.stringify(query));
  }

  // Add required parameters
  params.append("fields", fields);
  params.append("services_and_integrations_only_status", services_and_integrations_only_status.toString());

  // Pagination
  params.append("page", page.toString());
  params.append("size", size.toString());

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the companies.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let data = await response.json();
  // Return full pagination response with items, total, page, size
  return data;
}

// ### Fetching the company audit log (entity change history)
export async function fetchCompanyAudit({
  companyNameOrId,
  entityType,
  entityId,
  action,
  sourceSystemId,
  page = 1,
  size = 50,
  fields = "_id,entity_type,source_system_id,action,changed_fields,created_at",
  signal,
}: {
  companyNameOrId: string;
  entityType?: string;
  entityId?: string;
  action?: string;
  sourceSystemId?: string;
  page?: number;
  size?: number;
  fields?: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const params = new URLSearchParams();

  // Filters go through the MongoDB-style `query` param (same convention as
  // fetchCompanies); flat params like `entity_type=` are ignored by the backend.
  const query: Record<string, any> = {};
  if (entityType) query.entity_type = entityType;
  if (entityId) query.entity_id = entityId;
  if (action) query.action = action;
  if (sourceSystemId) query.source_system_id = sourceSystemId;
  if (Object.keys(query).length > 0) {
    params.append("query", JSON.stringify(query));
  }

  params.append("fields", fields);
  params.append("page", page.toString());
  params.append("size", size.toString());

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/audit?${params.toString()}`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the audit log.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching company listings
export async function fetchCompanyListings({
  companyNameOrId,
  sourceSystemId,
  street,
  number,
  page = 1,
  size = 50,
  fields = "_id,source_entities.source_system_id,property.address.street,property.address.number,property.address.zip_code,property.address.city",
  signal,
}: {
  companyNameOrId: string;
  sourceSystemId?: string;
  street?: string;
  number?: string;
  page?: number;
  size?: number;
  fields?: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const params = new URLSearchParams();

  // Same MongoDB-style `query` convention as the other endpoints. Street is a
  // regex (partial) match; the others are exact.
  const query: Record<string, any> = {};
  if (sourceSystemId) query["source_entities.source_system_id"] = sourceSystemId;
  if (street) query["property.address.street"] = { $regex: street, $options: "i" };
  if (number) query["property.address.number"] = number;
  if (Object.keys(query).length > 0) {
    params.append("query", JSON.stringify(query));
  }

  params.append("fields", fields);
  params.append("page", page.toString());
  params.append("size", size.toString());

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/listings?${params.toString()}`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the listings.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching a single listing (full document)
export async function fetchCompanyListing({
  companyNameOrId,
  listingId,
  signal,
}: {
  companyNameOrId: string;
  listingId: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/listings/${listingId}`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the listing.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching a listing's relationships
export async function fetchCompanyListingRelationships({
  companyNameOrId,
  listingId,
  signal,
}: {
  companyNameOrId: string;
  listingId: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/listings/${listingId}/relationships`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the listing relationships.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching a single audit log entry (full document)
export async function fetchCompanyAuditEntry({
  companyNameOrId,
  auditId,
  signal,
}: {
  companyNameOrId: string;
  auditId: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/audit/${auditId}`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the audit entry.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching a single company
export async function fetchCompany({ companyNameOrId, signal }: { companyNameOrId: string; signal?: any }) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}?show_credentials=false`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the company.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let company = await response.json();
  return company;
}

// ### Patching the company
export async function patchCompany({
  companyNameOrId,
  patch,
}: {
  companyNameOrId: string;
  patch: Partial<CompanyType>;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}`;
  const response = await fetch(url, {
    method: "PATCH",
    body: JSON.stringify(patch),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while patching the company.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  const updatedCompany = await response.json();
  return updatedCompany;
}

// ### Turning a failed company save into a user-facing message.
// Never include request echoes (FastAPI 422 items carry `input`, which can hold API keys): only `msg` is used.
const SAVE_FAILED_MESSAGE = "Saving the company failed. Please try again.";

export function formatSaveError(error: unknown): string {
  const info: unknown = error instanceof Error ? (error as HttpException).info : undefined;
  if (typeof info !== "object" || info === null) return SAVE_FAILED_MESSAGE;

  const detail = (info as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item: unknown) => (typeof item === "object" && item !== null ? (item as { msg?: unknown }).msg : undefined))
      .filter((msg): msg is string => typeof msg === "string")
      .map((msg) => msg.replace(/^Value error, /, ""));
    if (messages.length > 0) return messages.join("; ");
  }

  return SAVE_FAILED_MESSAGE;
}

// ### Checking whether a company's popup script exists in the GCS bucket
export interface PopupScriptStatus {
  url: string;
  status: "exists" | "missing" | "unknown";
  status_code?: number | null;
}

export async function checkPopupScript({
  companyNameOrId,
  signal,
}: {
  companyNameOrId: string;
  signal?: AbortSignal;
}): Promise<PopupScriptStatus> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/popup-script`;
  const response = await fetch(url, {
    headers: {
      Authorization: "Bearer " + token,
    },
    signal,
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while checking the popup script.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}

// ### Fetching a single company integration
export async function fetchCompanyIntegration({
  companyNameOrId,
  integrationName,
  signal,
}: {
  companyNameOrId: string;
  integrationName: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}?show_credentials=true`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the integration.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching a single company service config
export async function fetchCompanyServiceConfig({
  companyNameOrId,
  serviceName,
  signal,
}: {
  companyNameOrId: string;
  serviceName: string;
  signal?: AbortSignal;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/services/${serviceName}`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the service config.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching integration task types
export async function fetchIntegrationTaskTypes({
  companyNameOrId,
  integrationName,
  signal,
}: {
  companyNameOrId: string;
  integrationName: string;
  signal?: AbortSignal;
}): Promise<TaskTypesApiResponse> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/task-types?language=nl-BE`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the integration task types.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Fetching integration mappings
export async function fetchIntegrationMappings({
  companyNameOrId,
  integrationName,
  signal,
}: {
  companyNameOrId: string;
  integrationName: string;
  signal?: AbortSignal;
}): Promise<TaskTypeMapping[]> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/mappings`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the integration mappings.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Creating an integration mapping
export async function postIntegrationMapping({
  companyNameOrId,
  integrationName,
  mapping,
}: {
  companyNameOrId: string;
  integrationName: string;
  mapping: { mapping_type: string; mappings: Record<string, unknown> };
}): Promise<TaskTypeMapping> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/mappings`;

  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(mapping),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while creating the integration mapping.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Updating an integration mapping
export async function patchIntegrationMapping({
  companyNameOrId,
  integrationName,
  mappingId,
  update,
}: {
  companyNameOrId: string;
  integrationName: string;
  mappingId: string;
  update: { mappings: Record<string, unknown> };
}): Promise<TaskTypeMapping> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/mappings/${mappingId}`;

  const response = await fetch(url, {
    method: "PATCH",
    body: JSON.stringify(update),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while updating the integration mapping.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Triggering an integration poll
export async function triggerIntegrationPoll({
  companyNameOrId,
  integrationName,
  startTime,
  entityTypes,
}: {
  companyNameOrId: string;
  integrationName: string;
  startTime?: string;
  entityTypes?: string[];
}) {
  const token = await getToken();

  const body: { start_time?: string; entity_types?: string[] } = {};
  if (startTime) body.start_time = startTime;
  if (entityTypes && entityTypes.length > 0) body.entity_types = entityTypes;

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/poll`;
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException(`An error occurred while triggering the ${integrationName} poll.`);
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}

// ### Triggering an integration normalize
export async function triggerIntegrationNormalize({
  companyNameOrId,
  integrationName,
  startTime,
  entityTypes,
}: {
  companyNameOrId: string;
  integrationName: string;
  startTime?: string;
  entityTypes?: string[];
}) {
  const token = await getToken();

  const body: { start_time?: string; entity_types?: string[] } = {};
  if (startTime) body.start_time = startTime;
  if (entityTypes && entityTypes.length > 0) body.entity_types = entityTypes;

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/normalize`;
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException(`An error occurred while triggering the ${integrationName} normalization.`);
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}

// ### Creating the company
export async function postCompany({ name, website_url }: { name: string; website_url?: string }) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies`;
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify({ name, ...(website_url ? { website_url } : {}) }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while creating the company.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### File info model
export interface FileInfo {
  name: string;
  path: string;
  public_url: string;
  content_type?: string | null;
  size?: number | null;
  updated?: string | null;
}

// ### Listing company files
export async function fetchCompanyFiles({
  companyNameOrId,
  signal,
}: {
  companyNameOrId: string;
  signal?: AbortSignal;
}): Promise<FileInfo[]> {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/files`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching company files.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Uploading a company file
export async function uploadCompanyFile({
  companyNameOrId,
  file,
}: {
  companyNameOrId: string;
  file: File;
}): Promise<{ public_url: string }> {
  const token = await getToken();

  const formData = new FormData();
  formData.append("file", file);

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/files`;
  const response = await fetch(url, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: "Bearer " + token,
      // No Content-Type — browser sets multipart/form-data boundary automatically
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while uploading the file.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

// ### Activating integration pipeline
export async function activateIntegrationPipeline({
  companyNameOrId,
  integrationName,
  force = false,
}: {
  companyNameOrId: string;
  integrationName: string;
  force?: boolean;
}) {
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyNameOrId}/integrations/${integrationName}/activate?force=${force}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException(`An error occurred while activating the ${integrationName} integration pipeline.`);
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}
